// York-drop measurement calls. Both are fire-and-forget: the picker is
// walking to the drop zone whatever the network does, and a lost row is
// cheaper than putting an error between them and their next order. The
// BE mirrors this — its handlers swallow their own failures.
//
// Nothing downstream reads these rows; they exist to answer how many
// drop-zone trips actually happen, how long each takes and how far it
// is. See migration 166.
import { apiFetch } from "../../api/apiFetch";
import type { YorkDropReason } from "./YorkDropOff";

/**
 * Open a drop. Called when the drop screen APPEARS, not when the picker
 * arrives — the walk is what is being measured, so started_at has to
 * predate it.
 *
 * Returns the drop id, or null if it could not be opened (offline, or
 * the BE swallowed a failure), in which case the confirm is skipped.
 */
export const openYorkDrop = async (
    trigger: YorkDropReason,
    opts: { waveId?: number | string | null; fillQuarters?: number | null } = {},
): Promise<number | null> => {
    try {
        const res = await apiFetch("/picking/york/drop", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                trigger,
                waveId: opts.waveId ?? null,
                fillQuarters: opts.fillQuarters ?? null,
            }),
        });
        if (!res.ok) return null;
        const body = await res.json().catch(() => null);
        const id = body?.dropId;
        return typeof id === "number" ? id : null;
    } catch {
        return null;
    }
};

/**
 * Stamp the moment the picker was AT the drop zone. Splits the trip
 * into walk-out and walk-back-and-restart; the resume half is stamped
 * server-side when their next wave starts.
 */
export const confirmYorkDrop = async (dropId: number | null): Promise<void> => {
    if (dropId == null) return;
    try {
        await apiFetch(`/picking/york/drop/${dropId}/confirm`, { method: "POST" });
    } catch {
        /* measurement only — never surfaced to the picker */
    }
};
